// Polled gamepad source. Reads via StandardMapping so Xbox, DualShock/DualSense,
// and generic pads all land on the same indices. Produces analog move, a
// per-frame yaw delta, a button bitmask shaped for InputFrame, and a one-shot
// reset edge. Input.ts merges this with keyboard/mouse.

import { InputBtn } from './InputFrame';

const DEADZONE = 0.15;
const TRIGGER_THRESHOLD = 0.5;
const YAW_SPEED = 2.4; // rad/sec at full right-stick deflection
const PITCH_SPEED = 1.6; // rad/sec at full right-stick Y deflection

// Standard Mapping indices.
const AX_LX = 0, AX_LY = 1, AX_RX = 2, AX_RY = 3;
const BTN_A = 0, BTN_B = 1, BTN_X = 2, BTN_LT = 6, BTN_RT = 7, BTN_START = 9;

export interface GamepadPollResult {
  moveX: number;
  moveZ: number;
  yawDelta: number;
  pitchDelta: number;
  buttons: number;
  resetEdge: boolean;
  active: boolean;
}

export class Gamepad {
  private prevStart = false;
  private activeIndex = -1;
  private lastTimestamp = 0;
  private loggedFirstPoll = false;
  private loggedFirstInput = false;
  private pollCount = 0;

  constructor() {
    addEventListener('gamepadconnected', (e: any) => {
      const p = e.gamepad as globalThis.Gamepad;
      console.log('[gamepad] connected:', {
        index: p.index, id: p.id, mapping: p.mapping,
        axes: p.axes.length, buttons: p.buttons.length
      });
    });
    addEventListener('gamepaddisconnected', (e: any) => {
      const p = e.gamepad as globalThis.Gamepad;
      console.log('[gamepad] disconnected:', { index: p.index, id: p.id });
    });
  }

  poll(fixedDt: number): GamepadPollResult {
    this.pollCount += 1;
    if (this.pollCount === 60) {
      const pads = navigator.getGamepads?.() ?? [];
      const summary = Array.from(pads).map(p => p ? { index: p.index, id: p.id, connected: p.connected, mapping: p.mapping } : null);
      console.log('[gamepad] 1s poll check — getGamepads():', summary);
    }
    const pad = this.pickActivePad();
    if (pad && !this.loggedFirstPoll) {
      this.loggedFirstPoll = true;
      console.log('[gamepad] first active pad:', { id: pad.id, mapping: pad.mapping, axes: pad.axes.length, buttons: pad.buttons.length });
    }
    const result: GamepadPollResult = {
      moveX: 0, moveZ: 0, yawDelta: 0, pitchDelta: 0, buttons: 0, resetEdge: false, active: false
    };
    if (!pad) {
      this.prevStart = false;
      return result;
    }

    // Left stick → movement (radial deadzone).
    const lx = pad.axes[AX_LX] ?? 0;
    const ly = pad.axes[AX_LY] ?? 0;
    const lMag = Math.hypot(lx, ly);
    if (lMag > DEADZONE) {
      const scale = (lMag - DEADZONE) / (1 - DEADZONE) / lMag;
      result.moveX = lx * scale;
      result.moveZ = ly * scale; // forward is -Z, same as KeyW (-1) in Input.ts
      result.active = true;
    }

    // Right stick X → yaw delta (per-frame, integrated at sim-tick rate).
    const rx = pad.axes[AX_RX] ?? 0;
    if (Math.abs(rx) > DEADZONE) {
      const sign = Math.sign(rx);
      const mag = (Math.abs(rx) - DEADZONE) / (1 - DEADZONE);
      result.yawDelta = -sign * mag * YAW_SPEED * fixedDt;
      result.active = true;
    }

    // Right stick Y → pitch delta. Stick down (positive ry) tilts view DOWN
    // (camera orbits up and looks down at the character) — matches mouse.
    const ry = pad.axes[AX_RY] ?? 0;
    if (Math.abs(ry) > DEADZONE) {
      const sign = Math.sign(ry);
      const mag = (Math.abs(ry) - DEADZONE) / (1 - DEADZONE);
      result.pitchDelta = sign * mag * PITCH_SPEED * fixedDt;
      result.active = true;
    }

    // Buttons. Party-Animals-style layout:
    // - RT: Grab (engages both arms while held).
    // - LT: Sprint (run while held).
    // - A: jump.  X (Xbox) / Square (PS): punch.  B (Xbox) / Circle (PS): jump-kick / headbutt.
    let buttons = 0;
    if (pressed(pad, BTN_RT, TRIGGER_THRESHOLD)) buttons |= InputBtn.Grab;
    if (pressed(pad, BTN_LT, TRIGGER_THRESHOLD)) buttons |= InputBtn.Sprint;
    if (pressed(pad, BTN_A)) buttons |= InputBtn.Jump;
    if (pressed(pad, BTN_X)) buttons |= InputBtn.Punch;
    if (pressed(pad, BTN_B)) buttons |= InputBtn.JumpKick;
    result.buttons = buttons;
    if (buttons !== 0) result.active = true;

    if (result.active && !this.loggedFirstInput) {
      this.loggedFirstInput = true;
      console.log('[gamepad] first input detected:', {
        moveX: result.moveX.toFixed(2), moveZ: result.moveZ.toFixed(2),
        yawDelta: result.yawDelta.toFixed(3),
        buttons: '0b' + buttons.toString(2).padStart(5, '0')
      });
    }

    // Edge-detected reset (Start / Options).
    const startDown = pressed(pad, BTN_START);
    if (startDown && !this.prevStart) result.resetEdge = true;
    this.prevStart = startDown;
    if (startDown) result.active = true;

    return result;
  }

  private pickActivePad(): globalThis.Gamepad | null {
    const pads = navigator.getGamepads?.() ?? [];
    let best: globalThis.Gamepad | null = null;

    // Prefer the previously-active pad if still connected.
    if (this.activeIndex >= 0) {
      const p = pads[this.activeIndex];
      if (p && p.connected) best = p;
      else this.activeIndex = -1;
    }

    // Otherwise pick the most recently updated connected pad.
    if (!best) {
      let newestTs = -1;
      for (const p of pads) {
        if (!p || !p.connected) continue;
        if (p.timestamp > newestTs) { newestTs = p.timestamp; best = p; }
      }
      if (best) this.activeIndex = best.index;
    }

    if (best) this.lastTimestamp = best.timestamp;
    return best;
  }
}

function pressed(pad: globalThis.Gamepad, index: number, threshold = 0.5): boolean {
  const b = pad.buttons[index];
  if (!b) return false;
  return b.pressed || b.value > threshold;
}
